import React from 'react';
import { useForm, useFieldArray, Message, MultipleFieldErrors } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, Row, Col, Container, Button } from 'reactstrap';
import { RiAddLine, RiDeleteBinLine } from 'react-icons/ri';
import FormInput from '@/components/forms/FormInput';
import { AdminCountryInfoDto, CreateCountryDto, UpdateCountryDto } from '@repo/common-types';

type CountryFormData = CreateCountryDto;

interface CountryFormModalProps {
  onClose: () => void;
  onSubmit: (data: CreateCountryDto | UpdateCountryDto) => void;
  country?: AdminCountryInfoDto; // If provided, edit mode. If not provided, create mode

  isLoading?: boolean;
}

const errorMessageRenderer = (data: { message: Message; messages?: MultipleFieldErrors }) => {
  return <small className="text-danger">{data.message}</small>;
};

// Validation schema for country form
const countrySchema = Yup.object().shape({
  name: Yup.string()
    .required('Country name is required')
    .min(2, 'Country name must be at least 2 characters')
    .max(100, 'Country name cannot exceed 100 characters'),
  code: Yup.string()
    .required('Country code is required')
    .min(2, 'Country code must be at least 2 characters')
    .max(3, 'Country code cannot exceed 3 characters')
    .matches(/^[A-Z]+$/, 'Country code must contain only uppercase letters'),
  subdivisions: Yup.array()
    .of(
      Yup.object().shape({
        name: Yup.string()
          .required('Subdivision name is required')
          .min(2, 'Subdivision name must be at least 2 characters')
          .max(100, 'Subdivision name cannot exceed 100 characters'),
        code: Yup.string()
          .required('Subdivision code is required')
          .min(2, 'Subdivision code must be at least 2 characters')
          .max(10, 'Subdivision code cannot exceed 10 characters')
          .matches(/^[A-Z0-9-]+$/, 'Subdivision code must contain only uppercase letters, numbers, and hyphens'),
      }),
    )
    .optional(),
});

/**
 * Modal form for managing countries and their subdivisions
 */
const CountryFormModal: React.FC<CountryFormModalProps> = ({ country, onClose, onSubmit, isLoading = false }) => {
  // Country form
  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isDirty },
  } = useForm<CountryFormData>({
    resolver: yupResolver(countrySchema),
    defaultValues: {
      name: country?.name || '',
      code: country?.code || '',
      subdivisions: country?.subdivisions?.map((sub) => ({ name: sub.name, code: sub.code })) || [],
    },
    mode: 'onChange',
  });

  // Field array for subdivisions
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'subdivisions',
  });

  const onFormSubmit = (data: CountryFormData) => {
    onSubmit(data);
  };

  const handleModalClose = () => {
    onClose();
  };

  const handleAddSubdivision = () => {
    append({ name: '', code: '' });
  };

  const handleRemoveSubdivision = (index: number) => {
    remove(index);
  };

  const modalTitle = !country ? 'Add New Country' : `Edit Country: ${country.name}`;

  return (
    <Modal isOpen={true} toggle={handleModalClose} size="lg" centered className="country-modal">
      <Form onSubmit={handleSubmit(onFormSubmit)}>
        <ModalHeader toggle={handleModalClose} className="bg-dark p-4 text-white">
          {modalTitle}
        </ModalHeader>

        <ModalBody className="p-4">
          <Container fluid className="d-flex flex-column gap-4">
            {/* Country Form */}
            <Row className="g-4">
              <Col md={6}>
                <FormInput name="name" label="Country Name" control={control} placeholder="Enter country name..." />
              </Col>
              <Col md={6}>
                <FormInput label="Country Code" name="code" control={control} placeholder="e.g., US, CA, GB" />
              </Col>
            </Row>

            {/* Subdivisions Section */}
            <div className="border-top pt-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Subdivisions</h6>
                <Button type="button" color="primary" size="sm" onClick={handleAddSubdivision} disabled={isLoading}>
                  <RiAddLine className="me-1" />
                  Add Subdivision
                </Button>
              </div>

              {fields.length === 0 && (
                <p className="text-muted mb-0">
                  No subdivisions added. Click "Add Subdivision" to add states, provinces, or regions.
                </p>
              )}

              {fields.map((field, index) => (
                <Row key={field.id} className="g-3 mb-3">
                  <Col md={5}>
                    <FormInput
                      label="Subdivision Name"
                      name={`subdivisions.${index}.name`}
                      control={control}
                      placeholder="e.g., California, Ontario"
                      errorMessageRenderer={errorMessageRenderer}
                    />
                  </Col>
                  <Col md={5}>
                    <FormInput
                      label="Subdivision Code"
                      name={`subdivisions.${index}.code`}
                      control={control}
                      placeholder="e.g., CA, ON"
                      errorMessageRenderer={errorMessageRenderer}
                    />
                  </Col>
                  <Col md={2}>
                    <Button
                      type="button"
                      color="danger"
                      size="sm"
                      onClick={() => handleRemoveSubdivision(index)}
                      disabled={isLoading}
                    >
                      <RiDeleteBinLine />
                    </Button>
                  </Col>
                </Row>
              ))}
            </div>
          </Container>
        </ModalBody>

        <ModalFooter className="border-top p-4">
          <Button type="button" outline color="dark" onClick={handleModalClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" color="primary" disabled={isLoading || !isValid || !isDirty}>
            {isLoading ? 'Saving...' : !country ? 'Create Country' : 'Update Country'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default CountryFormModal;
